import { defineStore } from 'pinia';
import { ref, reactive, watch, computed } from 'vue';
import api from "src/services/api";
import merge from "deepmerge"
import {useWorkspacesStore} from 'stores/workspaces'
import {useUserStore} from "stores/user";

// TODO: Need to think about this. How can I make the client watch a change in a specific team member,
// and update that specific member. Maybe this file is not even needed. Maybe I can find a way watching the team in the
// workspaces.js store file.
// idea: keep 'dirty' flag per team member, and watch it
export const useTeamStore = defineStore('team', () => {
  // const teamMemberWorkspaces = reactive([])
  const workspacesStore = useWorkspacesStore()
  const userStore = useUserStore()
  /**
   *
   * @type {Ref<import('vue').UnwrapRef<TeamMember[]>>}
   */
  const team = ref([])
  const edited = ref([])
  const invites = ref([])
  const loadingTeam = ref(false)
  const loadingActive = ref(false) // this is read by auto-saver to make sure that we're not saving while loading the active item. Here we don't have active item as in forms and workspaces, but we still expose this so the auto-saver can remain generic for all data-stores.
  const saved = ref(true)
  const saving = ref(false)
  const saveWaitTimeout = ref(1)
  const userId = computed(() => userStore.user?.id)
  /** @type {Ref<TeamMember>} **/
  const currentTeamMember = computed(() => team.value.find(u => u.userId === userId.value))

  function $reset() {
    team.value = []
    edited.value = []
    loadingTeam.value = false
    loadingActive.value = false
    saved.value = true
    saving.value = false
    saveWaitTimeout.value = 1
  }

  function endLoading() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingTeam.value = false
    }, 10)
  }

  function endLoadingTeam() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingActive.value = false
    }, 10)
  }

  async function update() {
    loadingTeam.value = true // guard against triggering autosave due to response from save
    const updated = await api.updateTeam(workspacesStore.active.id, edited.value, team.value)
    updated.forEach(updatedTeamMember => {
      const teamIdx = team.value.findIndex(tm => tm.id === updatedTeamMember.id)
      if (teamIdx >= 0) {
        team.value[teamIdx] = updatedTeamMember
        edited.value[teamIdx] = merge({}, updatedTeamMember)
      }
    })
    endLoading.call(this)
  }

  async function loadTeam() {
    const workspaceId = workspacesStore?.active?.id
    if (!workspaceId) {
      return
    }
    loadingActive.value = true
    loadingTeam.value = true
    const {team:teamMembers, pendingInvites} = await api.listTeamMembers(workspaceId)
    team.value.splice(0, team.value.length, ...teamMembers)
    edited.value.splice(0, edited.value.length, ...teamMembers.map(member => merge({}, member)))
    endLoading.call(this)
    endLoadingTeam.call(this)
  }

  watch(
    () => workspacesStore.active,
    async () => {
      await loadTeam()
    },
    {immediate: true}
  )

  return {
    $reset,
    team,
    edited,
    loadingActive,
    loadingTeam,
    saved,
    saving,
    saveWaitTimeout,
    // Actions:
    loadTeam,
    update,
    currentTeamMember
  }
})


/**
 * @typedef TeamMember
 * @property {string} userId
 * @property {string} createTime
 * @property {string} updateTime
 * @property {string} memberSince
 * @property {string} name
 * @property {boolean} owner - whether this user is the owner of the workspace (i.e. the owner of the account that owns the workspace)
 * @property {Object} roles
 * @property {'NONE'|'VIEW'|'MANAGE'} roles.data
 * @property {'NONE'|'VIEW'|'MANAGE'} roles.forms
 * @property {'NONE'|'VIEW'|'INVITE'|'MANAGE'} roles.team
 * @property {'VIEW'|'MANAGE'} roles.workspace
 */
